/**
 * MacrosEditor — modal that owns the macro library document.
 *
 * Layout:
 *   ┌─────────── HEAD ───────────────────────────────────┐
 *   │ Macros editor             [save&close] [cancel]    │
 *   ├──────────┬──────────────────────────────────────────┤
 *   │ list     │  selected macro detail                   │
 *   │          │    name + triggerLengthBeats             │
 *   │  + add   │    [scrub bar — per-lane readout]        │
 *   │          │    LANE 1                                │
 *   │          │      target / length / shape / warps     │
 *   │          │      [curve preview SVG]                 │
 *   │          │    LANE 2  ...                           │
 *   │          │    [+ add lane]                          │
 *   └──────────┴──────────────────────────────────────────┘
 *
 * Edits are made on a local-copy draft.  Save commits to the store
 * (persists localStorage + pushes to bridge).  Cancel discards.
 */

import { createSignal, createMemo, For, Show, type Accessor } from 'solid-js';
import type {
  Macro,
  MacroLane,
  MacroLibrary,
  MacroShape,
  MacroShapeKind,
  MacroMidiKind,
  MacroMidiTarget,
  LaneDmxTarget,
} from '@opbuttons/schema';
import { evalShape, shapeToMidiValue, MACRO_SHAPE_PRESETS, isDmxLaneTarget } from '@opbuttons/schema';
import { LaneLightPicker, defaultLaneDmxTarget } from './LaneLightPicker.tsx';
import { macroLibrary, setMacroLibrary } from '../macros/store.ts';
import { ulid } from '../utils/ulid.ts';

const SHAPE_KINDS: MacroShapeKind[] = ['sine', 'exp', 'scurve', 'stepped', 'ramp', 'dip', 'hold'];
const MIDI_KINDS: MacroMidiKind[] = ['cc', 'noteon', 'noteoff'];

/** The target a lane had before it was switched to the other kind, so
 *  flipping MIDI <-> Light back restores it this session. Module-level, keyed
 *  by lane id: the lane editor remounts on every update (a new lane object),
 *  so a component-local signal would forget it after the first edit. */
const stashedTargets = new Map<string, MacroMidiTarget | LaneDmxTarget>();
const DEFAULT_MACRO_MIDI: MacroMidiTarget = { bridge: 'all', kind: 'cc', channel: 0, number: 1 };

interface MacrosEditorProps {
  onClose: () => void;
}

export function MacrosEditor(props: MacrosEditorProps) {
  // Draft = working copy. Mutated as the user edits. Save commits; cancel
  // discards. Deep-cloned so the underlying store is untouched until save.
  const [draft, setDraft] = createSignal<MacroLibrary>(
    structuredClone(macroLibrary()),
  );
  const [selectedId, setSelectedId] = createSignal<string | null>(
    macroLibrary().macros[0]?.id ?? null,
  );
  // Scrub position in beats relative to macro start. 0..triggerLength
  // by default, but extended to cover any lane that runs longer (tails).
  const [scrubBeats, setScrubBeats] = createSignal(0);

  const selected = createMemo<Macro | null>(() => {
    const id = selectedId();
    if (!id) return null;
    return draft().macros.find((m) => m.id === id) ?? null;
  });

  /** Total length of the scrub bar — max(trigger, longest lane) so tails
   *  remain visible. */
  const totalLengthBeats = createMemo(() => {
    const m = selected();
    if (!m) return 16;
    return Math.max(
      m.triggerLengthBeats,
      ...m.lanes.map((l) => l.lengthBeats),
    );
  });

  // — mutators ———————————————————————————————————————————————

  function updateMacro(patch: Partial<Macro>): void {
    const id = selectedId();
    if (!id) return;
    setDraft((prev) => ({
      ...prev,
      macros: prev.macros.map((m) => (m.id === id ? { ...m, ...patch } : m)),
    }));
  }

  function updateLane(laneId: string, patch: Partial<MacroLane>): void {
    const id = selectedId();
    if (!id) return;
    setDraft((prev) => ({
      ...prev,
      macros: prev.macros.map((m) => {
        if (m.id !== id) return m;
        return {
          ...m,
          lanes: m.lanes.map((l) => (l.id === laneId ? { ...l, ...patch } : l)),
        };
      }),
    }));
  }

  function updateShape(laneId: string, patch: Partial<MacroShape>): void {
    const m = selected();
    if (!m) return;
    const lane = m.lanes.find((l) => l.id === laneId);
    if (!lane) return;
    updateLane(laneId, { shape: { ...lane.shape, ...patch } });
  }

  function addMacro(): void {
    const next: Macro = {
      id: ulid(),
      name: 'New macro',
      triggerLengthBeats: 16,
      lanes: [
        {
          id: ulid(),
          label: 'CC 70',
          target: { bridge: 'pc', kind: 'cc', channel: 0, number: 70 },
          lengthBeats: 16,
          shape: { kind: 'sine' },
          center: 64,
          amplitude: 63,
        },
      ],
    };
    setDraft((prev) => ({ ...prev, macros: [...prev.macros, next] }));
    setSelectedId(next.id);
  }

  function removeMacro(): void {
    const id = selectedId();
    if (!id) return;
    setDraft((prev) => ({ ...prev, macros: prev.macros.filter((m) => m.id !== id) }));
    setSelectedId(draft().macros[0]?.id ?? null);
  }

  function addLane(): void {
    const m = selected();
    if (!m) return;
    const lane: MacroLane = {
      id: ulid(),
      label: `Lane ${m.lanes.length + 1}`,
      target: { bridge: 'pc', kind: 'cc', channel: 0, number: 70 + m.lanes.length },
      lengthBeats: m.triggerLengthBeats,
      shape: { kind: 'ramp' },
      center: 64,
      amplitude: 63,
    };
    updateMacro({ lanes: [...m.lanes, lane] });
  }

  function removeLane(laneId: string): void {
    const m = selected();
    if (!m) return;
    updateMacro({ lanes: m.lanes.filter((l) => l.id !== laneId) });
  }

  // — save / cancel ——————————————————————————————————————————

  function saveAndClose(): void {
    const next = draft();
    setMacroLibrary(next);
    props.onClose();
  }

  return (
    <div class="macros-editor-overlay" role="dialog" aria-label="Macros editor">
      <div class="macros-editor">
        <header class="me-head">
          <div class="me-title">Macros editor</div>
          <div class="me-actions">
            <button class="me-btn" type="button" onClick={props.onClose}>Cancel</button>
            <button class="me-btn me-btn-primary" type="button" onClick={saveAndClose}>Save</button>
          </div>
        </header>
        <div class="me-body">
          <aside class="me-sidebar">
            <div class="me-macro-list">
              <For each={draft().macros}>
                {(m) => (
                  <div
                    class="me-macro-row"
                    data-active={selectedId() === m.id ? 'true' : 'false'}
                    onClick={() => setSelectedId(m.id)}
                  >
                    <div>{m.name}</div>
                    <div class="me-macro-row-meta">{m.lanes.length}L · {m.triggerLengthBeats / 4}b</div>
                  </div>
                )}
              </For>
            </div>
            <button class="me-add-macro" type="button" onClick={addMacro}>+ new macro</button>
          </aside>
          <main class="me-detail">
            <Show
              when={selected()}
              fallback={<div class="me-detail-empty">Select a macro on the left, or create a new one.</div>}
            >
              {(macroAcc) => (
                <MacroDetail
                  macro={macroAcc}
                  totalLengthBeats={totalLengthBeats}
                  scrubBeats={scrubBeats}
                  setScrubBeats={setScrubBeats}
                  onUpdateMacro={updateMacro}
                  onUpdateLane={updateLane}
                  onUpdateShape={updateShape}
                  onAddLane={addLane}
                  onRemoveLane={removeLane}
                  onRemoveMacro={removeMacro}
                />
              )}
            </Show>
          </main>
        </div>
      </div>
    </div>
  );
}

// ====================================================================
// Macro detail (right pane)
// ====================================================================

interface MacroDetailProps {
  macro: Accessor<Macro>;
  totalLengthBeats: Accessor<number>;
  scrubBeats: Accessor<number>;
  setScrubBeats: (b: number) => void;
  onUpdateMacro: (patch: Partial<Macro>) => void;
  onUpdateLane: (laneId: string, patch: Partial<MacroLane>) => void;
  onUpdateShape: (laneId: string, patch: Partial<MacroShape>) => void;
  onAddLane: () => void;
  onRemoveLane: (laneId: string) => void;
  onRemoveMacro: () => void;
}

function MacroDetail(props: MacroDetailProps) {
  return (
    <>
      <section>
        <div class="me-field-row">
          <label class="me-field" style={{ flex: '1' }}>
            <span class="me-field-label">name</span>
            <input
              class="me-input"
              type="text"
              value={props.macro().name}
              onInput={(e) => props.onUpdateMacro({ name: e.currentTarget.value })}
            />
          </label>
          <label class="me-field">
            <span class="me-field-label">trigger length (bars)</span>
            <input
              class="me-input"
              type="number"
              min="1"
              max="64"
              value={props.macro().triggerLengthBeats / 4}
              onInput={(e) => {
                const bars = Math.max(1, Math.min(64, Number(e.currentTarget.value) || 1));
                props.onUpdateMacro({ triggerLengthBeats: bars * 4 });
              }}
            />
          </label>
          <button class="me-btn me-btn-danger" type="button" onClick={props.onRemoveMacro}>
            delete macro
          </button>
        </div>
      </section>

      <ScrubBar
        macro={props.macro}
        totalLengthBeats={props.totalLengthBeats}
        scrubBeats={props.scrubBeats}
        setScrubBeats={props.setScrubBeats}
      />

      <section>
        <div class="me-section-title">Lanes</div>
        <div style={{ display: 'flex', 'flex-direction': 'column', gap: '10px', 'margin-top': '8px' }}>
          <For each={props.macro().lanes}>
            {(lane) => (
              <LaneEditor
                lane={lane}
                scrubBeats={props.scrubBeats}
                onUpdate={(p) => props.onUpdateLane(lane.id, p)}
                onUpdateShape={(p) => props.onUpdateShape(lane.id, p)}
                onRemove={() => props.onRemoveLane(lane.id)}
              />
            )}
          </For>
        </div>
        <button class="me-add-lane" type="button" onClick={props.onAddLane} style={{ 'margin-top': '8px' }}>
          + add lane
        </button>
      </section>
    </>
  );
}

// ====================================================================
// Scrub bar — drag a playhead across bars, show per-lane MIDI value
// ====================================================================

interface ScrubBarProps {
  macro: Accessor<Macro>;
  totalLengthBeats: Accessor<number>;
  scrubBeats: Accessor<number>;
  setScrubBeats: (b: number) => void;
}

function ScrubBar(props: ScrubBarProps) {
  let barRef: HTMLDivElement | undefined;
  let dragging = false;

  function setFromClientX(clientX: number): void {
    if (!barRef) return;
    const rect = barRef.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const t = x / rect.width;
    const beats = t * props.totalLengthBeats();
    props.setScrubBeats(beats);
  }

  function onDown(e: PointerEvent): void {
    dragging = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setFromClientX(e.clientX);
  }
  function onMove(e: PointerEvent): void {
    if (!dragging) return;
    setFromClientX(e.clientX);
  }
  function onUp(e: PointerEvent): void {
    dragging = false;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch {}
  }

  const headPct = createMemo(() => {
    const total = props.totalLengthBeats();
    return total > 0 ? (props.scrubBeats() / total) * 100 : 0;
  });
  // Per-lane current MIDI value at scrub position.
  const readout = createMemo(() => {
    const beats = props.scrubBeats();
    return props.macro().lanes.map((lane) => {
      const t = Math.max(0, Math.min(1, beats / lane.lengthBeats));
      const past = beats > lane.lengthBeats;
      const v = shapeToMidiValue(evalShape(t, lane.shape), lane.center, lane.amplitude);
      return { lane, t, v, past };
    });
  });

  const triggerPct = createMemo(() => {
    const t = props.macro().triggerLengthBeats;
    const total = props.totalLengthBeats();
    return total > 0 ? (t / total) * 100 : 100;
  });

  return (
    <section class="me-scrub-wrap">
      <div class="me-section-title">Scrub playhead</div>
      <div
        ref={barRef}
        class="me-scrub-bar"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <div class="me-scrub-bar-fill" style={{ width: `${triggerPct()}%` }} />
        <div class="me-scrub-bar-head" style={{ left: `${headPct()}%` }} />
      </div>
      <div class="me-scrub-readout">
        <span>scrub: <b>{props.scrubBeats().toFixed(2)}</b> beats</span>
        <span>bar <b>{Math.floor(props.scrubBeats() / 4) + 1}</b>·{(((props.scrubBeats() % 4) + 1) | 0)}</span>
        <For each={readout()}>
          {(r) => (
            <span>
              {r.lane.label ?? 'lane'}: <b>{r.past ? '-' : r.v}</b>
              {r.past && <span style={{ color: 'var(--color-fg-ghost)' }}> (past)</span>}
            </span>
          )}
        </For>
      </div>
    </section>
  );
}

// ====================================================================
// Lane editor — target + shape + warps + curve preview
// ====================================================================

interface LaneEditorProps {
  lane: MacroLane;
  scrubBeats: Accessor<number>;
  onUpdate: (patch: Partial<MacroLane>) => void;
  onUpdateShape: (patch: Partial<MacroShape>) => void;
  onRemove: () => void;
}

function LaneEditor(props: LaneEditorProps) {
  // Cached preset names for the chip row. Mirrors MACRO_SHAPE_PRESETS keys.
  const presetEntries = Object.entries(MACRO_SHAPE_PRESETS) as [string, MacroShape][];

  return (
    <div class="me-lane">
      <div class="me-lane-head">
        <input
          class="me-input me-lane-name"
          type="text"
          value={props.lane.label ?? ''}
          onInput={(e) => props.onUpdate({ label: e.currentTarget.value })}
        />
        <button class="me-btn me-btn-danger" type="button" onClick={props.onRemove}>remove</button>
      </div>

      <div class="me-field-row">
        <label class="me-field" data-lane-sends="true">
          <span class="me-field-label">sends to</span>
          <select
            class="me-input"
            value={isDmxLaneTarget(props.lane.target) ? 'light' : 'midi'}
            onChange={(e) => {
              const cur = props.lane.target;
              const wantLight = e.currentTarget.value === 'light';
              if (wantLight === isDmxLaneTarget(cur)) return;
              const back = stashedTargets.get(props.lane.id);
              const next = wantLight
                ? (back && isDmxLaneTarget(back) ? back : defaultLaneDmxTarget())
                : (back && !isDmxLaneTarget(back) ? back : DEFAULT_MACRO_MIDI);
              if (!next) { e.currentTarget.value = 'midi'; return; }
              stashedTargets.set(props.lane.id, cur);
              props.onUpdate({ target: next });
            }}
          >
            <option value="midi">MIDI</option>
            <option value="light" disabled={!isDmxLaneTarget(props.lane.target) && defaultLaneDmxTarget() === null}>
              {defaultLaneDmxTarget() === null && !isDmxLaneTarget(props.lane.target) ? 'Light (none patched yet)' : 'Light'}
            </option>
          </select>
        </label>
        <Show
          when={(() => { const t = props.lane.target; return isDmxLaneTarget(t) ? t : null; })()}
          fallback={(() => {
            const midi = () => props.lane.target as MacroMidiTarget;
            return (
              <>
        <label class="me-field">
          <span class="me-field-label">midi kind</span>
          <select
            class="me-input"
            value={midi().kind}
            onChange={(e) => props.onUpdate({
              target: { ...midi(), kind: e.currentTarget.value as MacroMidiKind },
            })}
          >
            <For each={MIDI_KINDS}>{(k) => <option value={k}>{k}</option>}</For>
          </select>
        </label>
        <label class="me-field">
          <span class="me-field-label">channel</span>
          <input
            class="me-input"
            type="number" min="1" max="16"
            value={midi().channel + 1}
            onInput={(e) => props.onUpdate({
              target: { ...midi(), channel: Math.max(0, Math.min(15, (Number(e.currentTarget.value) || 1) - 1)) },
            })}
          />
        </label>
        <label class="me-field">
          <span class="me-field-label">{midi().kind === 'cc' ? 'cc number' : 'note'}</span>
          <input
            class="me-input"
            type="number" min="0" max="127"
            value={midi().number}
            onInput={(e) => props.onUpdate({
              target: { ...midi(), number: Math.max(0, Math.min(127, Number(e.currentTarget.value) || 0)) },
            })}
          />
        </label>
              </>
            );
          })()}
        >
          {(t) => (
            <LaneLightPicker
              target={t()}
              onChange={(next) => props.onUpdate({ target: next })}
              rowClass="me-field"
              labelClass="me-field-label"
              selectClass="me-input"
            />
          )}
        </Show>
        <label class="me-field">
          <span class="me-field-label">length (bars)</span>
          <input
            class="me-input"
            type="number" min="0.25" max="64" step="0.25"
            value={props.lane.lengthBeats / 4}
            onInput={(e) => {
              const bars = Math.max(0.25, Math.min(64, Number(e.currentTarget.value) || 1));
              props.onUpdate({ lengthBeats: bars * 4 });
            }}
          />
        </label>
      </div>

      <div>
        <div class="me-field-label" style={{ 'margin-bottom': '6px' }}>shape</div>
        <div class="me-shape-presets">
          <For each={SHAPE_KINDS}>
            {(k) => (
              <button
                class="me-shape-chip"
                type="button"
                data-active={props.lane.shape.kind === k ? 'true' : 'false'}
                onClick={() => props.onUpdateShape({ kind: k })}
              >
                {k}
              </button>
            )}
          </For>
          <span style={{ width: '12px' }} />
          <For each={presetEntries}>
            {([name, shape]) => (
              <button
                class="me-shape-chip"
                type="button"
                title={`preset: ${name}`}
                onClick={() => props.onUpdate({ shape: structuredClone(shape) })}
              >
                ✦ {name}
              </button>
            )}
          </For>
        </div>
      </div>

      <CurvePreview lane={props.lane} scrubBeats={props.scrubBeats} />

      <div class="me-slider-row">
        <div class="me-slider">
          <span class="me-slider-label">skew {props.lane.shape.skew?.toFixed(2) ?? '0.00'}</span>
          <input type="range" min="-1" max="1" step="0.05"
            value={props.lane.shape.skew ?? 0}
            onInput={(e) => props.onUpdateShape({ skew: Number(e.currentTarget.value) })} />
        </div>
        <div class="me-slider">
          <span class="me-slider-label">amplitude {((props.lane.shape.amplitude ?? 1) * 100).toFixed(0)}%</span>
          <input type="range" min="0" max="1" step="0.05"
            value={props.lane.shape.amplitude ?? 1}
            onInput={(e) => props.onUpdateShape({ amplitude: Number(e.currentTarget.value) })} />
        </div>
        <div class="me-slider">
          <span class="me-slider-label">offset {(props.lane.shape.offset ?? 0).toFixed(2)}</span>
          <input type="range" min="-1" max="1" step="0.05"
            value={props.lane.shape.offset ?? 0}
            onInput={(e) => props.onUpdateShape({ offset: Number(e.currentTarget.value) })} />
        </div>
        <div class="me-slider">
          <span class="me-slider-label">reverse</span>
          <label style={{ display: 'flex', 'align-items': 'center', gap: '6px', height: '28px' }}>
            <input type="checkbox"
              checked={props.lane.shape.reverse ?? false}
              onChange={(e) => props.onUpdateShape({ reverse: e.currentTarget.checked })} />
            <span style={{ 'font-family': 'var(--font-mono)' }}>{props.lane.shape.reverse ? 'on' : 'off'}</span>
          </label>
        </div>
      </div>

      <Show when={props.lane.shape.kind === 'stepped'}>
        <div class="me-field-row">
          <label class="me-field">
            <span class="me-field-label">steps</span>
            <input class="me-input" type="number" min="2" max="16"
              value={props.lane.shape.steps ?? 4}
              onInput={(e) => props.onUpdateShape({
                steps: Math.max(2, Math.min(16, Number(e.currentTarget.value) || 4)),
              })} />
          </label>
        </div>
      </Show>

      <div class="me-field-row">
        <label class="me-field">
          <span class="me-field-label">center (0-127)</span>
          <input class="me-input" type="number" min="0" max="127"
            value={props.lane.center}
            onInput={(e) => props.onUpdate({ center: Math.max(0, Math.min(127, Number(e.currentTarget.value) || 0)) })} />
        </label>
        <label class="me-field">
          <span class="me-field-label">amplitude (0-127)</span>
          <input class="me-input" type="number" min="0" max="127"
            value={props.lane.amplitude}
            onInput={(e) => props.onUpdate({ amplitude: Math.max(0, Math.min(127, Number(e.currentTarget.value) || 0)) })} />
        </label>
      </div>
    </div>
  );
}

// ====================================================================
// SVG curve preview — 60 samples across the lane, plotted to viewBox
// ====================================================================

interface CurvePreviewProps {
  lane: MacroLane;
  scrubBeats: Accessor<number>;
}

function CurvePreview(props: CurvePreviewProps) {
  const W = 600;
  const H = 90;
  // Path data — 60 samples across t=0..1.
  const path = createMemo(() => {
    const samples: string[] = [];
    const N = 60;
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      const v = evalShape(t, props.lane.shape);  // 0..1
      const x = (t * W).toFixed(1);
      const y = (H - v * H).toFixed(1);
      samples.push(`${i === 0 ? 'M' : 'L'}${x},${y}`);
    }
    return samples.join(' ');
  });
  // Scrub playhead position along the lane (capped to lane length).
  const scrubX = createMemo(() => {
    const t = Math.max(0, Math.min(1, props.scrubBeats() / props.lane.lengthBeats));
    return t * W;
  });
  const scrubV = createMemo(() => {
    const t = Math.max(0, Math.min(1, props.scrubBeats() / props.lane.lengthBeats));
    return evalShape(t, props.lane.shape);
  });

  return (
    <div class="me-curve">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        {/* baseline + midline */}
        <line x1="0" y1={H - 1} x2={W} y2={H - 1} stroke="var(--color-line-hairline)" />
        <line x1="0" y1={H / 2} x2={W} y2={H / 2} stroke="var(--color-line-hairline)" stroke-dasharray="4 4" />
        {/* curve */}
        <path d={path()} stroke="var(--color-idle)" stroke-width="2" fill="none" />
        {/* scrub marker */}
        <line x1={scrubX()} y1="0" x2={scrubX()} y2={H} stroke="var(--color-incoming)" stroke-width="1" opacity="0.7" />
        <circle cx={scrubX()} cy={H - scrubV() * H} r="3" fill="var(--color-incoming)" />
      </svg>
    </div>
  );
}
